/**
 * A compact relative-time label for a conversation row's last-activity
 * timestamp.
 *
 * Shared, by future import, with ISSUE-280's message timestamps, not a
 * Messages-tab-only helper, so nobody duplicates a second, slightly
 * different implementation later. Deliberately not localized
 * (`Intl.RelativeTimeFormat`): the compact style ("2h") matches the design
 * pack's own example and is the pattern to match, not a new i18n surface.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
const WEEK_MS = 604_800_000;

/**
 * `iso` is the timestamp being formatted; `nowMs` is the current time in
 * epoch milliseconds. A future `iso` (server clock skew) clamps to "now",
 * never a negative number.
 */
export function formatRelativeTime(iso: string, nowMs: number): string {
  const deltaMs = nowMs - new Date(iso).getTime();

  if (deltaMs < MINUTE_MS) return "now";
  if (deltaMs < HOUR_MS) return `${Math.floor(deltaMs / MINUTE_MS)}m`;
  if (deltaMs < DAY_MS) return `${Math.floor(deltaMs / HOUR_MS)}h`;
  if (deltaMs < WEEK_MS) return `${Math.floor(deltaMs / DAY_MS)}d`;

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(new Date(iso));
}

/** Clock time for the last message of a sender-run, e.g. "3:42 PM". */
export function formatMessageTime(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * The day divider's own label. "Same calendar day/year" compares in the
 * DEVICE's local time zone, via Date's own getFullYear/getMonth/getDate,
 * never a UTC comparison, so a message sent at 11:58 PM local time and one
 * at 12:02 AM local time land on different days, matching what the fan
 * actually sees on their clock.
 */
export function formatDayDividerLabel(iso: string, nowMs: number): string {
  const date = new Date(iso);
  const now = new Date(nowMs);

  if (isSameCalendarDay(date, now)) {
    return "Today";
  }

  const yesterday = new Date(now.getTime() - DAY_MS);
  if (isSameCalendarDay(date, yesterday)) {
    return "Yesterday";
  }

  if (date.getFullYear() === now.getFullYear()) {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    }).format(date);
  }

  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
}
